import input from "@krystal/css/components/input.css?inline";
import radioGroup from "@krystal/css/components/radio-group.css?inline";
import radio from "@krystal/css/components/radio.css?inline";
import { html, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@/components/alert";
import "@/components/button";
import "@/components/checkbox";
import "@/components/featured-icon";
import "@/components/icon";
import "@/components/input";
import "@/components/modal";
import "@/components/select";
import "@/components/textarea";
import "@/components/upload";
import EkoElement from "@/utilities/element";
import style from "./feedback-widget.css?inline";
import { BugFeedbackController } from "./screens/bug-feedback";
import { CsatFeedbackController } from "./screens/csat-feedback";
import { FeedbackTypeChoiceController } from "./screens/feedback-type-choice";
import { GeneralFeedbackController } from "./screens/general-feedback";
import { NpsFeedbackController } from "./screens/nps-feedback";
import { renderSuccess } from "./screens/success";
import {
  OPTIONS,
  type FeedbackWidgetCancelDetail,
  type FeedbackWidgetMode,
  type FeedbackWidgetSubmitDetail,
  type FeedbackWidgetValue,
} from "./types";

export type {
  FeedbackWidgetCancelDetail,
  FeedbackWidgetMode,
  FeedbackWidgetSubmitDetail,
  FeedbackWidgetValue,
};

@customElement("eko-feedback-widget")
class FeedbackWidget extends EkoElement {
  static styles = [
    ...EkoElement.styles,
    unsafeCSS(input),
    unsafeCSS(radio),
    unsafeCSS(radioGroup),
    unsafeCSS(style),
  ];

  private _onBack = () => {
    this._errorMessage = undefined;
    this._feedbackType = undefined;
    this._step = 1;
  };

  private _onCancel = (event: Event) => {
    event.preventDefault();
    const feedbackType: FeedbackWidgetCancelDetail["feedbackType"] =
      this.mode === "csat" || this.mode === "nps" ? this.mode : (this._feedbackType ?? null);
    this.open = false;
    this.dispatchEvent(
      new CustomEvent<FeedbackWidgetCancelDetail>("cancel", {
        bubbles: true,
        composed: true,
        detail: { feedbackType },
      })
    );
  };

  private _bugFeedback = new BugFeedbackController(this, {
    onBack: () => this._onBack(),
    onCancel: (e: Event) => this._onCancel(e),
    onSubmit: (d) => this._dispatchSubmit(d),
  });

  private _csatFeedback = new CsatFeedbackController(this, {
    onCancel: (e: Event) => this._onCancel(e),
    onSubmit: (d) => this._dispatchSubmit(d),
  });

  @state() private _errorMessage: string | undefined = undefined;

  @state() private _feedbackType: FeedbackWidgetValue | undefined = undefined;

  @state() private _step: 1 | 2 | 3 = 1;

  private _feedbackTypeChoice = new FeedbackTypeChoiceController(this, {
    onCancel: (e: Event) => this._onCancel(e),
    onNext: () => {
      this._step = 2;
    },
    onSelect: (v) => {
      this._feedbackType = v;
    },
  });

  private _generalFeedback = new GeneralFeedbackController(this, {
    onBack: () => this._onBack(),
    onCancel: (e: Event) => this._onCancel(e),
    onSubmit: (d) => this._dispatchSubmit(d),
  });

  @state() private _loading = false;

  private _npsFeedback = new NpsFeedbackController(this, {
    onCancel: (e: Event) => this._onCancel(e),
    onSubmit: (d) => this._dispatchSubmit(d),
  });

  private _onSuccessClose = (event: Event) => {
    event.preventDefault();
    this.open = false;
  };

  @property({ attribute: "follow-up-negative" }) followUpNegative: string | undefined = undefined;

  @property({ attribute: "follow-up-neutral" }) followUpNeutral: string | undefined = undefined;

  @property({ attribute: "follow-up-positive" }) followUpPositive: string | undefined = undefined;

  @property() heading = "We would love to hear your feedback!";

  @property() icon = "message-circle-01";

  @property({ reflect: true }) mode: FeedbackWidgetMode = "default";

  @property({ reflect: true, type: Boolean }) open = false;

  @property() question = "How did we do?";

  @property({ attribute: "supporting-text" }) supportingText =
    "What type of feedback you have for us?";

  private _dispatchSubmit(detail: FeedbackWidgetSubmitDetail) {
    this._loading = true;
    this._errorMessage = undefined;
    this.dispatchEvent(
      new CustomEvent<FeedbackWidgetSubmitDetail>("submit", {
        bubbles: true,
        composed: true,
        detail,
      })
    );
  }

  private renderFeedbackFlow() {
    if (this._step === 1) {
      return this._feedbackTypeChoice.render({
        heading: this.heading,
        icon: this.icon,
        supportingText: this.supportingText,
        value: this._feedbackType,
      });
    }
    if (this._step === 2) {
      return this._feedbackType === "bug"
        ? this._bugFeedback.render({
            errorMessage: this._errorMessage,
            loading: this._loading,
          })
        : this._generalFeedback.render({
            errorMessage: this._errorMessage,
            icon: OPTIONS.find((o) => o.value === this._feedbackType)?.icon ?? this.icon,
            loading: this._loading,
          });
    }
    return renderSuccess(this._onSuccessClose);
  }

  private renderSurveyContent(isCsat: boolean) {
    if (this._step === 3) return renderSuccess(this._onSuccessClose);
    const followUpParams = {
      errorMessage: this._errorMessage,
      followUpNegative: this.followUpNegative,
      followUpNeutral: this.followUpNeutral,
      followUpPositive: this.followUpPositive,
      loading: this._loading,
      question: this.question,
    };
    return isCsat
      ? this._csatFeedback.render(followUpParams)
      : this._npsFeedback.render(followUpParams);
  }

  render() {
    const isCsat = this.mode === "csat";
    const isNps = this.mode === "nps";
    const isSurvey = isCsat || isNps;
    const layout = isSurvey
      ? this._step === 3
        ? "centered"
        : "default"
      : this._step === 2
        ? "default"
        : "centered";

    return html`
      <eko-modal layout=${layout} ?open=${this.open} @cancel=${this._onCancel}>
        ${isSurvey ? this.renderSurveyContent(isCsat) : this.renderFeedbackFlow()}
      </eko-modal>
    `;
  }

  showError(message = "Something went wrong. Please try again.") {
    this._loading = false;
    this._errorMessage = message;
  }

  showSuccess() {
    this._loading = false;
    this._step = 3;
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    if (changedProperties.has("_step") && this._step === 2) {
      (this._feedbackType === "bug" ? this._bugFeedback : this._generalFeedback).requestFocus();
    }

    if (changedProperties.has("open") && !this.open) {
      this._loading = false;
      this._errorMessage = undefined;
      this._step = 1;
      this._feedbackType = undefined;
      this._bugFeedback.reset();
      this._csatFeedback.reset();
      this._generalFeedback.reset();
      this._npsFeedback.reset();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-feedback-widget": FeedbackWidget;
  }
}

export default FeedbackWidget;
